import { useState, useRef, useEffect } from "react";
import { Loader2, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { apiService } from "../services/api";
import { useToast } from "../hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { MessageList } from "../components/message/MessageList";
import { Message, SuccessResponse } from "../types/generated";
import { useTranslation } from "react-i18next";

const Messages = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const limit = 20;
  const [confirmSend, setConfirmSend] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["messages"],
    queryFn: async ({ pageParam = 1 }) => {
      return apiService.getMessages(pageParam, limit);
    },
    getNextPageParam: (lastPage: SuccessResponse<Message[]>, allPages) => {
      const total = Number(lastPage.data.length);
      const totalPages = Math.ceil(total / limit);
      const nextPage = allPages.length + 1;
      return nextPage <= totalPages ? nextPage : undefined;
    },
    initialPageParam: 1,
  });

  const messages = data?.pages.flatMap((page) => page.data) || [];

  const handleSendMessage = async (messageId: number) => {
    try {
      await apiService.sendMessage(messageId);
      toast({
        title: t("common.success"),
        description: t("message.sent_successfully"),
      });
      refetch();
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("message.error_sending_message") + " " + t("common.try_again"),
      });
    } finally {
      setConfirmSend(null);
    }
  };

  const handleDeleteMessage = async (messageId: number) => {
    try {
      await apiService.deleteMessage(messageId);
      toast({
        title: t("common.success"),
        description: t("message.deleted_successfully"),
      });
      refetch();
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("message.error_deleting_message") + " " + t("common.try_again"),
      });
    } finally {
      setConfirmDelete(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl relative">
        <div className="text-center text-red-500">
          <p>{t("message.error_loading_messages")}</p>
        </div>
      </div>
    );
  }

  const draftMessages = messages.filter((m) => m.status === "pending") || [];
  const scheduledMessages =
    messages.filter((m) => m.status === "scheduled") || [];
  const sentMessages = messages.filter((m) => m.status === "sent") || [];

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">{t("pages.messages.title")}</h1>
      </div>

      <Tabs defaultValue="drafts" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="drafts">{t("pages.messages.drafts")}</TabsTrigger>
          <TabsTrigger value="scheduled">{t("pages.messages.scheduled")}</TabsTrigger>
          <TabsTrigger value="sent">{t("pages.messages.sent")}</TabsTrigger>
        </TabsList>

        <TabsContent value="drafts">
          <MessageList
            title={t("pages.messages.drafts_title")}
            messages={draftMessages}
            onSendClick={(id) => setConfirmSend(id)}
            isDraft={true}
            hasMore={hasNextPage}
            isLoadingMore={isFetchingNextPage}
            onLoadMore={() => fetchNextPage()}
            onDeleteClick={(id) => setConfirmDelete(id)}
          />
        </TabsContent>

        <TabsContent value="scheduled">
          <MessageList
            title={t("pages.messages.scheduled_title")}
            messages={scheduledMessages}
            onSendClick={(id) => setConfirmSend(id)}
            hasMore={hasNextPage}
            isLoadingMore={isFetchingNextPage}
            onLoadMore={() => fetchNextPage()}
            onDeleteClick={(id) => setConfirmDelete(id)}
          />
        </TabsContent>

        <TabsContent value="sent">
          <MessageList
            title={t("pages.messages.sent_title")}
            messages={sentMessages}
            hasMore={hasNextPage}
            isLoadingMore={isFetchingNextPage}
            onLoadMore={() => fetchNextPage()}
            onDeleteClick={(id) => setConfirmDelete(id)}
          />
        </TabsContent>
      </Tabs>

      <Dialog
        open={confirmSend !== null}
        onOpenChange={() => setConfirmSend(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("pages.messages.confirm_send_title")}</DialogTitle>
            <DialogDescription>
              {t("pages.messages.confirm_send_description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmSend(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => confirmSend && handleSendMessage(confirmSend)}
            >
              {t("common.send")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmDelete !== null}
        onOpenChange={() => setConfirmDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("pages.messages.confirm_delete_title")}</DialogTitle>
            <DialogDescription>
              {t("pages.messages.confirm_delete_description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                confirmDelete && handleDeleteMessage(confirmDelete)
              }
            >
              {t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Button
        onClick={() => navigate("/messages/create")}
        size="icon"
        className="fixed bottom-6 left-6 h-14 w-14 rounded-full shadow-lg hover:shadow-xl"
      >
        <Plus className="h-6 w-6" />
      </Button>
    </div>
  );
};

export default Messages;
